<template>
  <div class="container-fluid">
    <div v-if="isPurchaseFormActive">
      <purchase-approval-form
        :item="order"
        @saved="onCloseCallOut"
        @close="
          () => {
            activeForm = null;
          }
        "
      />
    </div>
    <page-header
      :page="header.page"
      :actions="header.actions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    >
    </page-header>
    <request-to-purchase
      v-if="showRequestToPurchase"
      :order-id="orderId"
      @close="onRequestToPurchaseClosed"
    />

    <board class="my-3" :details="boardDetails">
      <template #data-item="{ item }">
        <div v-if="!item.data">
          <custom-balloon
            :background-color="item.statusColor"
            :text="item.statusName"
            dot
          />
        </div>
      </template>
    </board>
    <div v-if="order?.id">
      <div class="my-2">
        <q-tab :active-tab="activeTab">
          <q-tab-item
            lazy
            :tabindex="0"
            title="Disponible en CSEE/ORE"
            @click="setCurrentTab(0)"
          >
          </q-tab-item>
          <q-tab-item
            lazy
            :tabindex="1"
            title="Fuera de CSEE/ORE"
            @click="setCurrentTab(1)"
          >
          </q-tab-item>
        </q-tab>
        <book-search
          :is-out-of-center="isOutOfCenter"
          @refresh="refresh"
          :order="order"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "PageHeader";
import Board from "Board";
import { QTab, QTabItem } from "@/components/common/q-tabs";
import CustomBalloon from "custom-balloon";
import BookSearch from "./components/BookSearch.vue";
import RequestToPurchase from "./components/RequestToPurchase.vue";
import PurchaseApprovalForm from "./components/PurchaseApprovalForm.vue";
import storeMixin from "@/mixin/storeGenerator";
import alertMixin from "@/mixin/alert.mixin";
import service from "@/services/api/inventoryOrderBooks.api";
import { name as appModuleName } from "@/modules/app/store/index";
import { mapGetters } from "vuex";
import TABS from "@/utils/constants/inventory/inventoryOrderBooksCenterTabs";
import { studentBookStatus } from "@/utils/constants/book/studentBookStatus.js";

const TEMPLATE_ACTION_INDEX = {
  APPROVEPURCHASE: 0
};

export default {
  name: "InventoryRequestsBooks",
  mixins: [storeMixin, alertMixin],
  components: {
    PageHeader,
    Board,
    QTab,
    QTabItem,
    CustomBalloon,
    BookSearch,
    RequestToPurchase,
    PurchaseApprovalForm
  },
  data() {
    return {
      order: {},
      activeTab: Number(this.$route.query.tab ?? TABS.In),
      showRequestToPurchase: false,
      activeForm: null
    };
  },
  async mounted() {
    this.activeTab = this.tabParam;
    await this.getOrder();
  },
  computed: {
    ...mapGetters(appModuleName, { isMobile: "getIsMobileDensity" }),
    orderId() {
      return Number(this.$route.params.id);
    },
    tabParam() {
      return Number(this.$route.query.tab ?? TABS.In);
    },
    header() {
      return {
        page: {
          icon: "fas fa-list-ul",
          title: `Manejo de Disponibilidad: ${this.order.book?.name}`,
          routes: [
            "Inicio",
            "Libros",
            "Manejo de Solicitudes de Libros",
            `Manejo de Disponibilidad`
          ]
        },
        actions: [
          {
            text: "Regresar",
            icon: "fas fa-arrow-circle-left",
            variant: "outline-success",
            click: () => this.$router.go(-1)
          },
          {
            text: "Solicitar para Compra",
            icon: "far fa-plus-circle",
            variant: "success",
            visible:
              !this.showRequestToPurchase &&
              !this.order?.hasPurchases &&
              !this.order?.bookId,
            click: () => (this.showRequestToPurchase = true)
          },
          {
            text: "Aprobar Compra",
            icon: "far fa-plus-circle",
            variant: "success",
            visible: this.isPurchasebuttonVisible,
            click: () => {
              this.activeForm = TEMPLATE_ACTION_INDEX.APPROVEPURCHASE;
            }
          }
        ]
      };
    },
    boardDetails() {
      return [
        [
          {
            label: "Estatus:",
            statusColor: this.order.statusColor,
            statusName: this.order.status?.name,
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Número de Solictud:",
            data: this.order.requestCode ?? "-",
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Título de Libro:",
            data: this.order.book?.name ?? "-",
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Materia:",
            data: this.order.subject?.name ?? "-",
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Medio de Lectura:",
            data: this.order.readingMedium?.name ?? "-",
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Grado:",
            data: this.order.grade?.name ?? "-",
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Centro:",
            data: this.order.center?.name ?? "-",
            cols: "col-12 col-md-3 pt-2"
          },
          {
            label: "Escuela:",
            data: this.order.school?.name ?? "-",
            cols: "col-12 col-md-3 pt-2"
          }
        ]
      ];
    },
    isPurchasebuttonVisible() {
      return (
        studentBookStatus.PedienteDeterminacionSAEE == this.order?.status?.id &&
        this.userHasPermissions("academic.books.requests.approvepurchase")
      );
    },
    isPurchaseFormActive() {
      return this.activeForm == TEMPLATE_ACTION_INDEX.APPROVEPURCHASE;
    },
    isOutOfCenter() {
      return this.activeTab === TABS.Out;
    }
  },
  methods: {
    async getOrder() {
      const { data } = await service.get(this.orderId);
      this.order = data;
    },
    setCurrentTab(tab) {
      const sameRoute = this.tabParam === tab;
      if (sameRoute) return;

      this.activeTab = tab;
      if (tab) this.$router.replace({ query: { tab } });
      else this.$router.replace({ name: this.$route.name });
    },
    onRequestToPurchaseClosed(submitted) {
      this.showRequestToPurchase = false;
      if (submitted) this.$router.go(-1);
    },
    async onCloseCallOut() {
      this.activeForm = null;
      await this.getOrder();
    },
    async refresh() {
      await this.getOrder();
    }
  }
};
</script>

<style scoped></style>
